import CallIcon from '@mui/icons-material/Call';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import { Box, Container, Divider, Grid2 as Grid, IconButton, Stack, Typography } from '@mui/material';
import { useContext } from 'react';
import { FaFacebookF, FaInstagram } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { productURL } from '../../utils/functions/common';
import { Product } from '../../utils/models/products';
import { ProductsStore } from '../../utils/stores/ProductsStore';
import { baseProducts } from '../../utils/stores/productCodes';

const headingSx = {
    color: 'white',
    fontSize: '0.8rem',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    mb: 2.5,
} as const;

const footerLinkSx = {
    color: 'rgba(255,255,255,0.72)',
    textDecoration: 'none',
    fontSize: '0.95rem',
    transition: 'color .2s ease',
    width: 'fit-content',
    '&:hover': {
        color: 'primary.main',
    },
} as const;

const socialButtonSx = {
    color: 'white',
    border: '1px solid rgba(255,255,255,0.25)',
    borderRadius: '50%',
    width: 40,
    height: 40,
    transition: 'all .2s ease',
    '&:hover': {
        backgroundColor: 'primary.main',
        borderColor: 'primary.main',
        color: 'white',
    },
} as const;

export default function Footer() {
    const productController = useContext(ProductsStore);

    const productLinks = baseProducts
        .filter((p) => p.tags.includes('featureTile') || p.tags.includes('popular'))
        .map((p) => productController?.products?.find((prod) => prod.productCode === p.productCode))
        .filter((prod): prod is Product => Boolean(prod))
        .map((prod) => ({ text: prod.name, link: productURL(prod) }));

    const companyLinks = [
        { text: 'Home', link: '/' },
        { text: 'Our Fleet', link: '/fleet' },
        { text: 'FAQ', link: '/faq' },
        { text: 'Contact Us', link: '/contactus' },
        { text: 'Terms & Conditions', link: '/termsandconditions' },
    ];

    const contactItems = [
        {
            icon: <LocationOnOutlinedIcon sx={{ fontSize: 19 }} />,
            text: '83 Royal Flying Doctor St, Cairns QLD 4870',
            href: 'https://goo.gl/maps/LQDRrKGD5kQnmtG39',
            external: true,
        },
        {
            icon: <CallIcon sx={{ fontSize: 19 }} />,
            text: '07 4015 4191',
            href: 'tel:0740154191',
        },
        {
            icon: <SmsOutlinedIcon sx={{ fontSize: 19 }} />,
            text: '+61 483 966 894',
            subtitle: 'SMS or WhatsApp — calls not supported',
            href: 'https://wa.me/61483966894',
            external: true,
        },
        {
            icon: <EmailOutlinedIcon sx={{ fontSize: 19 }} />,
            text: 'cairns@gslaviation.com.au',
            href: 'mailto:cairns@gslaviation.com.au',
        },
    ];

    return (
        <Box component="footer" sx={{ backgroundColor: 'background.dark', color: 'white', pt: { xs: 5, md: 7 } }}>
            <Container maxWidth="lg">
                <Grid container spacing={{ xs: 4, md: 3 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography
                            component={Link}
                            to="/"
                            sx={{
                                display: 'inline-block',
                                color: 'white',
                                fontSize: '1.5rem',
                                fontWeight: 700,
                                letterSpacing: '0.02em',
                                textDecoration: 'none',
                                mb: 2,
                            }}
                        >
                            GSL Aviation
                        </Typography>
                        <Typography
                            sx={{
                                color: 'rgba(255,255,255,0.65)',
                                fontSize: '0.95rem',
                                lineHeight: 1.7,
                                maxWidth: 320,
                                mb: 3,
                            }}
                        >
                            Scenic flights and reef tours over Cairns and the Great Barrier Reef. Experience tropical
                            North Queensland from the air.
                        </Typography>
                        <Stack direction="row" spacing={1.5}>
                            <IconButton
                                component="a"
                                href="https://www.facebook.com/GSLAir/"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="GSL Aviation on Facebook"
                                sx={socialButtonSx}
                            >
                                <FaFacebookF size={18} />
                            </IconButton>
                            <IconButton
                                component="a"
                                href="https://www.instagram.com/gsl_aviation/"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="GSL Aviation on Instagram"
                                sx={socialButtonSx}
                            >
                                <FaInstagram size={18} />
                            </IconButton>
                        </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                        <Typography sx={headingSx}>Tours</Typography>
                        <Stack spacing={1.25}>
                            {productLinks.map((link) => (
                                <Typography key={link.text} component={Link} to={link.link || '/'} sx={footerLinkSx}>
                                    {link.text}
                                </Typography>
                            ))}
                        </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, md: 2 }}>
                        <Typography sx={headingSx}>Company</Typography>
                        <Stack spacing={1.25}>
                            {companyLinks.map((link) => (
                                <Typography key={link.text} component={Link} to={link.link} sx={footerLinkSx}>
                                    {link.text}
                                </Typography>
                            ))}
                        </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                        <Typography sx={headingSx}>Contact</Typography>
                        <Stack spacing={1.25}>
                            {contactItems.map((item) => (
                                <Box
                                    key={item.href}
                                    component="a"
                                    href={item.href}
                                    {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                    sx={{
                                        ...footerLinkSx,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.25,
                                        '&:hover .footer-contact-icon': { color: 'primary.main' },
                                    }}
                                >
                                    <Box
                                        className="footer-contact-icon"
                                        sx={{
                                            display: 'flex',
                                            color: 'primary.main',
                                            mt: '1px',
                                            transition: 'color .2s ease',
                                        }}
                                    >
                                        {item.icon}
                                    </Box>
                                    <Box component="span">
                                        {item.text}
                                        {'subtitle' in item && item.subtitle && (
                                            <Typography
                                                component="span"
                                                display="block"
                                                sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', mt: 0.25 }}
                                            >
                                                {item.subtitle}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Stack>
                    </Grid>
                </Grid>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mt: { xs: 4, md: 6 } }} />

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        py: 3,
                    }}
                >
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                        {`Copyright © GSL Aviation ${new Date().getFullYear()}`}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                        Cairns, Queensland, Australia
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
}
